import { useCallback, useEffect } from 'react';
import { PrimitiveAtom, useAtomValue, useSetAtom } from 'jotai';
import { atom } from 'jotai';
import queryString from 'query-string';
import { updateUrl } from '@/utils';

const nullNumberAtom = atom<number>(0);

export type PointCloudDeeplinkState = {
  shouldUpdateUrl: boolean;
  pointBudgetAtom: PrimitiveAtom<number>;
  enableAdaptivePointBudgetAtom: PrimitiveAtom<boolean>;
  edlStrengthAtom?: PrimitiveAtom<number>;
  rgbBlendLevelAtom?: PrimitiveAtom<number>;
};

export const usePointCloudDeeplink = (state: PointCloudDeeplinkState) => {
  const {
    shouldUpdateUrl,
    pointBudgetAtom,
    enableAdaptivePointBudgetAtom,
    edlStrengthAtom,
    rgbBlendLevelAtom,
  } = state;

  const pointBudget = useAtomValue(pointBudgetAtom);
  const enableAdaptivePointBudget = useAtomValue(enableAdaptivePointBudgetAtom);
  const edlStrengthRaw = useAtomValue(edlStrengthAtom ?? nullNumberAtom);
  const rgbBlendLevelRaw = useAtomValue(rgbBlendLevelAtom ?? nullNumberAtom);

  const setPointBudget = useSetAtom(pointBudgetAtom);
  const setEnableAdaptivePointBudget = useSetAtom(enableAdaptivePointBudgetAtom);
  const setEdlStrengthRaw = useSetAtom(edlStrengthAtom ?? nullNumberAtom);
  const setRgbBlendLevelRaw = useSetAtom(rgbBlendLevelAtom ?? nullNumberAtom);

  useEffect(() => {
    if (shouldUpdateUrl) {
      updateUrl(
        'pointBudget',
        enableAdaptivePointBudget ? 'auto' : Math.round(pointBudget / 1_000_000).toString()
      );
    }
  }, [shouldUpdateUrl, pointBudget, enableAdaptivePointBudget]);

  useEffect(() => {
    if (shouldUpdateUrl && edlStrengthAtom) {
      updateUrl('edlStrength', edlStrengthRaw.toString());
    }
  }, [shouldUpdateUrl, edlStrengthAtom, edlStrengthRaw]);

  useEffect(() => {
    if (shouldUpdateUrl && rgbBlendLevelAtom) {
      updateUrl('rgbBlendLevel', rgbBlendLevelRaw.toString());
    }
  }, [shouldUpdateUrl, rgbBlendLevelAtom, rgbBlendLevelRaw]);

  const applyFromParsed = useCallback(
    (parsed: queryString.ParsedQuery<string>) => {
      if (parsed.pointBudget && !Array.isArray(parsed.pointBudget)) {
        if (parsed.pointBudget === 'auto') {
          setEnableAdaptivePointBudget(true);
        } else {
          const budget = Math.max(1, Math.min(30, Number.parseInt(parsed.pointBudget)));
          setEnableAdaptivePointBudget(false);
          setPointBudget(budget * 1_000_000);
        }
      }

      if (parsed.edlStrength && typeof parsed.edlStrength === 'string' && edlStrengthAtom) {
        setEdlStrengthRaw(Number.parseFloat(parsed.edlStrength));
      }

      if (parsed.rgbBlendLevel && typeof parsed.rgbBlendLevel === 'string' && rgbBlendLevelAtom) {
        setRgbBlendLevelRaw(Number.parseFloat(parsed.rgbBlendLevel));
      }
    },
    [
      edlStrengthAtom,
      rgbBlendLevelAtom,
      setEnableAdaptivePointBudget,
      setPointBudget,
      setEdlStrengthRaw,
      setRgbBlendLevelRaw,
    ]
  );

  return { applyFromParsed };
};
